import { push, replace } from 'connected-react-router';
// jl-design-system
import { getFormInitialValuesFromAddress } from 'jl-design-system/utils/address/address';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import {
  resetAddressSearch,
} from 'jl-design-system/redux/actions/address/addressActions';
// lodash
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';

import {
  initSavedCollectionPoint,
  getCollectionPointDates,
  getSavedCollectionPoints,
} from '../click-and-collect/clickAndCollectActions';
import { initPaymentPage } from '../payment/orderFormActions';
import { createAddress } from '../addressBook/addressBookActions';
import { fetchPaymentWallet } from '../user/userActions';
import getUndeliverableItems from '../../../utils/delivery/getUndeliverableItems';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import routeConstants from '../../../constants/routeConstants';
import userConstants from '../../../constants/userConstants';
import {
  getDeliveryAddressPayloadFromFormValues,
} from '../../../utils/address/addressHelpers';
import {
  getDeliveryInstructions,
  isSameAddress,
  getGiftMessageLines,
} from '../../../utils';
import shouldSkipDeliveryPage from '../../../utils/delivery/shouldSkipDeliveryPage';
import {
  ANALYTICS_DELIVERY_INSTRUCTIONS_SELECTED,
  ANALYTICS_DELIVERY_OPTIONS,
  ANALYTICS_GIFT_MESSAGE_POPULATED,
  ANALYTICS_GIFT_RECEIPT_SELECTED,
  ANALYTICS_PUT_DELIVERY_DETAILS,
  ANALYTICS_SHOW_AGE_CHECK_MODAL,
  ANALYTICS_UPDATED_PRODUCT_PAYMENT_DATA,
  CHANGE_DELIVERY_ADDRESS,
  EDIT_DELIVERY_ADDRESS,
  GET_DELIVERY_METHODS,
  GET_DELIVERY_PAGE,
  POST_DELIVERY_PAGE,
  PUT_DELIVERY_ADDRESS,
  PUT_DELIVERY_DETAILS,
  SILENTLY_SELECT_COLLECTION_DATE,
  RESET_DELIVERY_METHODS,
  SET_DELIVERY_BATCHES_PROCESSING,
  SET_SELECTED_DATE_METHOD,
  SET_SELECTED_DATE_METHOD_PAGE,
  SET_SELECTED_DELIVERY_CHOICE_ID,
  SET_SELECTED_SLOT_METHOD,
  SET_SHOW_DELIVERY_ADDRESS_FORM,
  USER_AGE_CHECK,
  FULFILMENT_CHOICE_IGNORED,
} from '../../../constants/actionConstants';
import { DELIVERY_ADDRESS_FORM_ID } from '../../../utils/form/configs/deliveryAddress';
import featureConstants from '../../../constants/featureConstants';
import deliveryConstants, { DELIVERY_DETAILS_FORM_ID } from '../../../constants/deliveryConstants';
import {
  URL_GET_DELIVERY_PAGE,
  URL_PUT_DELIVERY_ADDRESS,
  URL_GET_DELIVERIES_METHODS_V3,
  URL_POST_DELIVERY_PAGE,
  URL_PUT_DELIVERY_DETAILS,
} from '../../../constants/endpointConstants';
import { triggerAnalyticsEvent } from '../analytics/analyticsAction';
import { handleSubmitFormValidationErrorAnalytics, processForms } from '../form/formActions';
import { setScrollToDeliveryDetails } from '../app/appActions';
import daysBetweenDateAndStartOfWeek from '../../../utils/helpers/date/daysBetweenDateAndStartOfWeek';
import handleInconsistentOrderFormState from '../../../utils/orderform/handleInconsistentOrderFormState';
import { canMakeApplePayPayments } from '../payment/applePayActions';
import { showBatchingFailureModal } from '../edit-basket/editBasketActions';
import { isFeatureActive } from '../../reducers/config/configReducer';

export const getDeliveryPage = eventDetails => async (dispatch, getState) => {
  const state = getState();
  if (get(state, 'app.orderComplete', false)) {
    return {};
  }

  const defaultDeliveryTabEnabled = isFeatureActive(state, featureConstants.DEFAULT_DELIVERY_TAB_ENABLED);

  const dispatchObject = {
    type: GET_DELIVERY_PAGE,
    request: client => client({ path: URL_GET_DELIVERY_PAGE, config: { method: 'GET' } }),
    ...defaultDeliveryTabEnabled && { defaultDeliveryTabEnabled },
  };

  if (eventDetails) {
    return dispatch({
      ...dispatchObject,
      eventDetails,
    });
  }

  return dispatch(dispatchObject);
};

const setUpdatedProductAndPaymentAnalytics = (deliveryMethodsResponse = []) => (dispatch) => {
  const oldPaymentAnalytics = window?.jlData?.checkout?.payment;
  const oldProductAnalytics = window?.jlData?.checkout?.product;

  const productAnalytics = flatten(deliveryMethodsResponse.map(response => get(response, 'result.analytics.checkout.product', []).map((product) => {
    const foundProduct = oldProductAnalytics.filter(item => item.sku === product.sku)[0];

    if (foundProduct) {
      return {
        ...foundProduct,
        batchNum: response.batchId,
      };
    }

    return product;
  })));

  return dispatch({
    type: ANALYTICS_UPDATED_PRODUCT_PAYMENT_DATA,
    paymentAnalytics: oldPaymentAnalytics,
    productAnalytics,
  });
};

export const postDeliveryPage = () => async (dispatch, getState) => {
  const state = getState();
  if (get(state, 'app.orderComplete', false)) return {};

  const defaultDeliveryTabEnabled = isFeatureActive(state, featureConstants.DEFAULT_DELIVERY_TAB_ENABLED);
  const fulfilmentPreferenceFeatureActive = isFeatureActive(state, featureConstants.ENABLE_FULFILMENT_PREFERENCE);

  return dispatch({
    type: POST_DELIVERY_PAGE,
    request: client => client({ path: URL_POST_DELIVERY_PAGE, config: { method: 'POST' } }),
    ...defaultDeliveryTabEnabled && { defaultDeliveryTabEnabled },
    ...fulfilmentPreferenceFeatureActive && { fulfilmentPreferenceFeatureActive },
  });
};

export const clearDeliveryDetailsValidationErrors = formId => (dispatch, getState) => {
  dispatch(resetForm(formId, get(getState().form, `${formId}.values`, {})));
};

export const getDeliveryMethodsAnalytics = deliveries => (dispatch) => {
  const analytics = deliveries.map((delivery) => {
    if (!delivery.analytics?.deliveryProposition) {
      return null;
    }

    return {
      ...delivery.analytics,
      deliveryProposition: delivery.analytics.deliveryProposition.map((deliveryProposition, index) => {
        const {
          availabilityByDayDates,
          availabilityByDaySlots,
          ...otherProps
        } = deliveryProposition;

        const updatedDeliveryProposition = otherProps;

        if (availabilityByDayDates || availabilityByDaySlots) {
          // analytics expects an 'availabilityByDay' field but BFF returns either 'availabilityByDayDates' or
          // 'availabilityByDaySlots' depending on the type of calendar.
          // Slots should be an array of arrays, dates is a string
          updatedDeliveryProposition.availabilityByDay = availabilityByDayDates || [availabilityByDaySlots];
        }

        if (availabilityByDaySlots) {
          // GVF analytics expects 'dateRange' to be a string
          updatedDeliveryProposition.dateRange = delivery.dateRange.toString();
        }

        const selected = index === delivery.selectedMethod ? 1 : 0;

        return {
          ...updatedDeliveryProposition,
          selected,
        };
      }),
    };
  });

  dispatch(triggerAnalyticsEvent(ANALYTICS_DELIVERY_OPTIONS, {
    analytics: {
      checkout: {
        batch: analytics,
      },
    },
  }));
};

export const updateDeliveryMethodAnalytics = () => async (dispatch, getState) => {
  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export function getBatchedDeliveryFormsWithErrorsIds(deliveries = [], state) {
  return deliveries.reduce((acc, batch) => {
    const formId = `${DELIVERY_DETAILS_FORM_ID}-${batch.id}`;
    const form = get(state.form, formId, {});
    const hasErrors = !isEmpty(form.syncErrors);
    return hasErrors ? [...acc, formId] : acc;
  }, []);
}

export const setDeliveryBatchesProcessing = isProcessing => ({
  type: SET_DELIVERY_BATCHES_PROCESSING,
  isProcessing,
});

export const putDeliveryDetails = submitPayload => ({
  type: PUT_DELIVERY_DETAILS,
  request: client => client({
    path: URL_PUT_DELIVERY_DETAILS,
    config: { method: 'PUT', body: { deliveries: submitPayload } },
  }),
});

export const getDeliveryDetailsForSubmit = (delivery, values = {}) => {
  const {
    selectedDeliveryMethodId,
    deliveryType,
  } = delivery;

  const { deliveryMethod: formValueDeliveryMethod, giftReceipt, giftMessage, ...rest } = values;

  const formValues = {
    ...rest,
    giftMessageLines: getGiftMessageLines(giftReceipt, giftMessage),
    deliveryMethod: selectedDeliveryMethodId || formValueDeliveryMethod,
  };

  const isGreenVanFleet = deliveryType ===
    deliveryConstants.GREEN_VAN_FLEET ||
    delivery.type === deliveryConstants.GREEN_VAN_FLEET;

  const deliveryInstructionsPresent = (formValues.deliveryInstructionsOption?.length > 0);

  const deliveryInstructions = isGreenVanFleet && deliveryInstructionsPresent ?
    getDeliveryInstructions(formValues) : {};

  const { giftMessageLines } = formValues;

  let deliveryDetails;
  let parsedValues;
  try {
    parsedValues = JSON.parse(values[deliveryConstants.DELIVERY_V3_FIELD]);

    deliveryDetails = {
      fulfilment: {
        methodId: parsedValues.methodId,
        fulfilmentOfferId: parsedValues.fulfilmentOfferId,
        ...giftMessageLines && { giftMessageLines },
        ...!isEmpty(deliveryInstructions) && { deliveryInstructions },
      },
    };
  } catch (e) {
    return {};
  }

  // Get analytics details
  let analyticsEventDetails;

  const selectedMethod = delivery.methods[delivery.selectedMethod || 0];

  if (selectedMethod.fulfilmentOffers.type === deliveryConstants.DELIVERY_TYPE_STANDARD) {
    analyticsEventDetails = {
      deliveryMessage: selectedMethod.description.toLowerCase(),
    };
  } else {
    analyticsEventDetails = {
      deliveryMessage: selectedMethod.description.toLowerCase(),
      firstAvailableDay: daysBetweenDateAndStartOfWeek(
        selectedMethod.fulfilmentOffers.rows.map(r => r.offers.find(o => o.available))[0]?.date,
      ),
      selectedDaysUntilDelivery: daysBetweenDateAndStartOfWeek(parsedValues.date),
    };
  }

  return [{
    deliveryId: delivery.id,
    fulfilment: deliveryDetails.fulfilment,
  }, { ...analyticsEventDetails }];
};

export const submitDeliveryDetails = (shouldFollowFulfilmentPreference = false) => async (dispatch, getState) => {
  const state = getState();
  const deliveries = state.delivery.deliveries;

  const formsWithErrorsIds = getBatchedDeliveryFormsWithErrorsIds(deliveries, state);

  if (formsWithErrorsIds.length) {

    let formSyncErrors = {};

    formsWithErrorsIds.forEach((formId) => {
      formSyncErrors[formId] = get(state, `form.${formId}.syncErrors`);
    });

    const processFormsErrors = await dispatch(processForms(formsWithErrorsIds[0], formsWithErrorsIds));

    formSyncErrors = {
      ...processFormsErrors,
    };

    await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
    return;
  }

  dispatch(setDeliveryBatchesProcessing(true));

  const analyticsEventDetails = [];
  const deliveryDetailsPayload = [];

  deliveries.forEach((delivery) => {
    const formId = delivery.deliveryDetailsFormId;
    const form = get(state.form, formId, {});

    const returnValues = getDeliveryDetailsForSubmit(
      delivery,
      form.values,
    );

    deliveryDetailsPayload.push(returnValues[0]);
    if (returnValues.length > 0) {
      analyticsEventDetails.push(returnValues[1]);
    }
  });

  const putDeliveryDetailsResponse = await dispatch(putDeliveryDetails(deliveryDetailsPayload));

  if (putDeliveryDetailsResponse.type === `${PUT_DELIVERY_DETAILS}.SUCCESS`) {
    analyticsEventDetails.forEach((analytics) => {
      dispatch(triggerAnalyticsEvent(ANALYTICS_PUT_DELIVERY_DETAILS, {
        ...analytics,
      }));
    });

    dispatch(initPaymentPage({ pushAfter: true, shouldFollowFulfilmentPreference }));
  }

  if (
    putDeliveryDetailsResponse.type === `${PUT_DELIVERY_DETAILS}.FAILED` &&
    get(putDeliveryDetailsResponse, 'error.code') === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
  ) {
    const rebatchInProgress = get(state, 'orderForm.rebatchOrderApiCallActive');
    if (!rebatchInProgress) {
      await dispatch(handleInconsistentOrderFormState());
    }
  }

  dispatch(setDeliveryBatchesProcessing(false));
};

export const putDeliveryAddress = (body, isSavedAddress = false) => ({
  type: PUT_DELIVERY_ADDRESS,
  request: client => client({ path: URL_PUT_DELIVERY_ADDRESS, config: { method: 'PUT', body } }),
  address: body.addressDetails,
  isSavedAddress,
});

export const getDeliveryMethods = ({
  address,
  fromDate,
  id,
  refresh,
  dateRange,
}) => (dispatch) => {
  if (refresh) {
    dispatch(resetForm(`${DELIVERY_DETAILS_FORM_ID}-${id}`, {}));
  }

  return dispatch({
    address,
    type: GET_DELIVERY_METHODS,
    batchId: id,
    ...fromDate && { fromDate },
    ...refresh && { refresh },
    ...dateRange && { dateRange },
    mergeCustomerInfo: true,
    request: client => client({ path: URL_GET_DELIVERIES_METHODS_V3({ from: fromDate, id }), config: { method: 'GET' } }),
  });
};

export const refreshDeliveryMethods = () => async (dispatch, getState) => {
  const { confirmedDeliveryAddress, deliveries } = getState().delivery;
  const { items } = getState().orderForm;

  if (!isEmpty(getUndeliverableItems(deliveries, items))) {
    // don't get delivery methods until undeliverable items in basket scenario is resolved
    return;
  }

  const getDeliveryMethodsResponse = await Promise.all(deliveries.map(async (delivery) => {
    const {
      deliveryType,
      id,
    } = delivery;

    let fromDate = '';
    if (deliveryType === deliveryConstants.GREEN_VAN_FLEET || delivery.type === deliveryConstants.GREEN_VAN_FLEET) {
      if (!delivery.selectedMethodIsExpress) {
        fromDate = delivery.fromDate;
      }
    }

    const response = await dispatch(getDeliveryMethods({
      address: confirmedDeliveryAddress,
      fromDate,
      id,
    }));

    if (
      response.type === `${GET_DELIVERY_METHODS}.FAILED` &&
      get(response, 'error.code') === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
    ) {
      await dispatch(handleInconsistentOrderFormState());
    }

    return response;
  }));

  dispatch(setUpdatedProductAndPaymentAnalytics(getDeliveryMethodsResponse));

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setDeliveryAddressAndGetDeliveryMethods = ({
  formValues,
  address,
}) => (dispatch, getState) => (
  new Promise(async (resolve) => {
    const { app, delivery } = getState();

    const isSavedAddress = !!(get(address, 'id') || get(formValues, 'id'));
    const confirmedDeliveryAddress = get(delivery, 'confirmedDeliveryAddress');
    const shouldSetConfirmedDeliveryAddressAgain = get(delivery, 'shouldSetConfirmedDeliveryAddressAgain');
    const disableSaveDeliveryAddress =
      get(app, 'orderComplete', false) || get(app, 'orderProcessing', false);

    if (disableSaveDeliveryAddress) {
      resolve({});
      return;
    }

    if (
      !shouldSetConfirmedDeliveryAddressAgain &&
      isSavedAddress &&
      isSameAddress({ formValues, address, addressToCheck: confirmedDeliveryAddress })
    ) {
      resolve({});
      return;
    }

    const addressFromFormValues = getDeliveryAddressPayloadFromFormValues(formValues);
    const body = address ?
      { addressDetails: address } : { addressDetails: addressFromFormValues };

    if (body.addressDetails.invalid) {
      resolve({});
      return;
    }

    const putDeliveryAddressResponse = await dispatch(
      putDeliveryAddress(
        body,
        isSavedAddress,
      ),
    );

    if (putDeliveryAddressResponse.type === `${PUT_DELIVERY_ADDRESS}.SUCCESS`) {
      const deliveries = get(putDeliveryAddressResponse, 'result.orderForm.deliveries');
      const items = get(putDeliveryAddressResponse, 'result.orderForm.items');

      if (!isEmpty(getUndeliverableItems(deliveries, items))) {
        // don't get delivery methods until undeliverable items in basket scenario is resolved
        dispatch(push(routeConstants.DELIVERY));
        resolve();
        return;
      }

      const getDeliveryMethodsResponse = await Promise.all(deliveries.map(async (delivery) => {
        dispatch(resetForm(delivery.deliveryDetailsFormId));
        const deliveryMethods = await dispatch(getDeliveryMethods({
          address: isEmpty(address) ? addressFromFormValues : address,
          id: delivery.id,
          type: delivery.type,
          isSavedAddress,
        }));

        return deliveryMethods;
      }));

      dispatch(setUpdatedProductAndPaymentAnalytics(getDeliveryMethodsResponse));

      dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
    }

    resolve(putDeliveryAddressResponse);
  })
);

export const resetDeliveryMethods = () => ({
  type: RESET_DELIVERY_METHODS,
});

export const resetDeliveryAddressAndMethods = ({ addressId, countryCode }) => async (dispatch) => {
  dispatch(resetDeliveryMethods());
  dispatch(resetAddressSearch({ addressId, countryCode }));
};

export const initSignedInUser = () => async (dispatch, getState) => {
  const user = getState().user || {};
  if (user.isSignedInWithData) {
    const selectedDeliveryAddress = user.selectedDeliveryAddress;

    if (selectedDeliveryAddress) {
      if (
        !selectedDeliveryAddress.invalid &&
        !selectedDeliveryAddress.notAvailableForDelivery
      ) {
        await dispatch(setDeliveryAddressAndGetDeliveryMethods({ address: selectedDeliveryAddress }));
      }
    }
  }
};

export const setCollectionUrl = redirectAction => async (dispatch, getState) => {
  // TODO fix this so it doesn't close the map view on page refresh
  // https://www.jlpit.com/jira/browse/MARV-10538
  const selectedCollectionPointId = get(getState(), 'clickAndCollect.selectedCollectionPoint.id');
  let url;
  if (selectedCollectionPointId) {
    url = routeConstants.CLICK_AND_COLLECT_STORE_SELECTED;
  } else {
    url = routeConstants.CLICK_AND_COLLECT_SEARCH_LIST;
  }

  return dispatch(redirectAction(url));
};

export const tryToSkipDeliveryPage = () => async (dispatch, getState) => {
  let firstAvailableDay;

  const delivery = get(getState(), 'delivery.deliveries[0]', {});
  const deliveryMethods = delivery.methods || [];
  const standardDeliveryExists = deliveryMethods.some(
    method => method.fulfilmentOffers.type === deliveryConstants.DELIVERY_TYPE_STANDARD &&
      !!method.fulfilmentOffers.fulfilmentOfferId,
  );

  // standard delivery fulfilmentOfferId already set in DeliveryMethodText or CalendarDates.
  // if standard delivery is unavailable (e.g. tradeplace or madeToMeasure with calendar) then
  // silently select first available date here

  if (!standardDeliveryExists) {
    const firstCalendarMethod = deliveryMethods.find(
      method => method.fulfilmentOffers.type === deliveryConstants.DELIVERY_TYPE_CALENDAR,
    );

    if (firstCalendarMethod) {
      const containsMonthLabel = !!firstCalendarMethod.fulfilmentOffers.monthLabel;
      if (!containsMonthLabel) {
        // select the first available day
        firstCalendarMethod.fulfilmentOffers.rows.some((row) => {
          if (!firstAvailableDay) {
            firstAvailableDay = row.offers.find(offer => offer.available);
            return true;
          }

          return false;
        });

        if (firstAvailableDay) {
          dispatch({
            type: '@@redux-form/CHANGE',
            meta: {
              field: deliveryConstants.DELIVERY_V3_FIELD,
              form: `${DELIVERY_DETAILS_FORM_ID}-${delivery.id}`,
            },
            payload: JSON.stringify({
              deliveryId: delivery.id,
              deliveryOn: firstAvailableDay.deliveryOn,
              methodId: firstCalendarMethod.methodId,
              fulfilmentOfferId: firstAvailableDay.fulfilmentOfferId,
            }),
          });
        }
      }
    }
  }

  if (standardDeliveryExists || firstAvailableDay) {
    await dispatch(submitDeliveryDetails(true));
  }
};

// TODO a lot of common functionality between initDelivery and setSelectedDeliveryChoiceId, could possibly be merged
export const initDelivery = (refresh = false) => async (dispatch, getState) => {
  const state = getState();
  const selectedDeliveryChoiceId = get(state, 'delivery.selectedDeliveryChoiceId');

  if (selectedDeliveryChoiceId === deliveryConstants.DELIVERY) {
    // if delivery methods exist in state we can assume
    // user has manually changed URL to get here, so don't redirect

    if (!some(state.delivery.deliveries, 'methods')) {
      await dispatch(replace(routeConstants.DELIVERY));
    }

    const confirmedDeliveryAddress = get(state, 'delivery.confirmedDeliveryAddress');
    const getDeliveryMethodsError = get(state, 'delivery.getDeliveryMethodsError');
    const putDeliveryAddressError = get(state, 'delivery.putDeliveryAddressError');

    if (!getDeliveryMethodsError && !putDeliveryAddressError) {
      const hasConfirmedDeliveryAddress = !!confirmedDeliveryAddress;

      if (hasConfirmedDeliveryAddress) {
        const shouldSetConfirmedDeliveryAddressAgain = get(state, 'delivery.shouldSetConfirmedDeliveryAddressAgain');

        if (shouldSetConfirmedDeliveryAddressAgain) {
          dispatch(setScrollToDeliveryDetails(true));
          await dispatch(setDeliveryAddressAndGetDeliveryMethods({
            address: confirmedDeliveryAddress,
          }));
        } else {
          await dispatch(refreshDeliveryMethods());
          if (shouldSkipDeliveryPage(getState())) {
            dispatch(tryToSkipDeliveryPage());
          } else if (includes([
            userConstants.FULFILMENT_PREFERENCE_DELIVERY,
            userConstants.FULFILMENT_PREFERENCE_COLLECTION,
          ], getState().user?.customerPreferences?.fulfilmentChoice)) {
            dispatch({
              type: FULFILMENT_CHOICE_IGNORED,
            });
          }
        }
      } else {
        dispatch(initSignedInUser());
      }
    }
  }

  if (selectedDeliveryChoiceId === deliveryConstants.CLICK_AND_COLLECT) {
    if (state.user?.collectionPointsSaved && !state.user.savedCollectionPointsLoaded) {
      await dispatch(getSavedCollectionPoints());
    }
    await dispatch(setCollectionUrl(replace));
    dispatch(initSavedCollectionPoint(refresh));
  }

};

export const setSelectedDeliveryChoiceId = id => async (dispatch, getState) => {

  const state = getState();
  const deliveryMethodsApiCallComplete = state.delivery?.deliveryMethodsApiCallComplete;

  dispatch({
    type: SET_SELECTED_DELIVERY_CHOICE_ID,
    id,
    resetUseDeliveryAddressAsBillingAddress: get(state.delivery, 'selectedDeliveryChoiceId') !== id,
  });

  if (id === deliveryConstants.DELIVERY) {
    dispatch(push(routeConstants.DELIVERY));
    dispatch(initDelivery());
  }

  if (id === deliveryConstants.CLICK_AND_COLLECT) {
    if (state.user?.collectionPointsSaved && !state.user?.savedCollectionPointsLoaded) {
      await dispatch(getSavedCollectionPoints());
    }

    await dispatch(setCollectionUrl(push));
    dispatch(initSavedCollectionPoint());

    const selectedCollectionPointId = get(state, 'clickAndCollect.selectedCollectionPoint.id');
    if (deliveryMethodsApiCallComplete && selectedCollectionPointId) {
      const batchId = state.delivery.deliveries[0]?.id;

      const fetchedCollectionPointDates = await dispatch(getCollectionPointDates({
        batchId,
        collectionPointId: selectedCollectionPointId,
      }));

      const newSelectedCollectionDateObject = find(fetchedCollectionPointDates.result.collectionDates,
        item => item.date === state.clickAndCollect.selectedCollectionTime);

      if (newSelectedCollectionDateObject) {
        dispatch({
          type: SILENTLY_SELECT_COLLECTION_DATE,
          payload: newSelectedCollectionDateObject.date,
        });
      }

    }
  }
};

export const initDeliveryPage = (refresh = false) => async (dispatch, getState) => {
  // occurs if browser is refreshed OR user navigates via browser controls
  const postOnly = get(getState(), 'delivery.usePostToInitDeliveryPage', true);
  const response = await dispatch(postOnly ? postDeliveryPage() : getDeliveryPage());

  if (response.type === `${GET_DELIVERY_PAGE}.SUCCESS` || response.type === `${POST_DELIVERY_PAGE}.SUCCESS`) {
    const { showAgeCheckModal, userDobInvalid, userDob } = getState().user || {};

    if (showAgeCheckModal) {
      dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_AGE_CHECK_MODAL));
      if (userDobInvalid) {
        dispatch({
          type: `${USER_AGE_CHECK}.FAILED`,
          dob: userDob,
        });
      }
    }

    if (
      get(getState(), 'delivery.undeliverableItemsInBasket', []).length > 0 ||
      get(getState(), 'delivery.tradeplaceItemsWithUndeliverableQuantity', []).length > 0
    ) {
      // TODO remove the DELIVERY_CHOICE route as it no longer serves a purpose
      if (get(getState(), 'router.location.pathname') === routeConstants.DELIVERY_CHOICE) {
        await dispatch(replace(routeConstants.DELIVERY));
      }
      return;
    }

    dispatch(canMakeApplePayPayments());
    dispatch(fetchPaymentWallet());

    await dispatch(initDelivery(refresh));
  }

  if (
    response.type === `${GET_DELIVERY_PAGE}.FAILED` &&
    get(response, 'error.code') === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
  ) {
    await dispatch(handleInconsistentOrderFormState());
  }

  if (response.type === `${POST_DELIVERY_PAGE}.FAILED` &&
    get(response, 'error.code') === errorCodeConstants.BATCHING_FAILURE
  ) {
    const batchingFailureIds = get(response.error, 'inconsistentBatchProducts', []);
    await dispatch(showBatchingFailureModal(batchingFailureIds));
  }
};

export const getDeliveryMethodsDateRange = ({ id, fromDate, refresh, dateRange }) => async (dispatch, getState) => {
  await dispatch(getDeliveryMethods({
    id,
    fromDate,
    refresh,
    dateRange,
  }));

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setSelectedSlotMethod = (displayMethod, express, delivery) => (dispatch, getState) => {
  dispatch({
    type: SET_SELECTED_SLOT_METHOD,
    displayMethod,
    express,
    delivery,
  });

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setSelectedDateMethod = (displayMethod, delivery) => (dispatch, getState) => {
  dispatch({
    type: SET_SELECTED_DATE_METHOD,
    displayMethod,
    delivery,
  });

  dispatch(getDeliveryMethodsAnalytics(getState().delivery.deliveries));
};

export const setSelectedDateMethodPage = ({ delivery, method, newPage, shouldAnimate }) => ({
  type: SET_SELECTED_DATE_METHOD_PAGE,
  delivery,
  method,
  newPage,
  shouldAnimate,
});

export const deliveryInstructionsOptionSelected = selection => (dispatch) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_DELIVERY_INSTRUCTIONS_SELECTED, { subType: selection }));
};

export const editDeliveryAddress = address => async (dispatch) => {
  dispatch(resetForm(DELIVERY_ADDRESS_FORM_ID, getFormInitialValuesFromAddress(address)));
  dispatch({ type: EDIT_DELIVERY_ADDRESS });
};

export const changeDeliveryAddress = () => async (dispatch, getState) => {

  const isSignedInWithData = get(getState().user, 'isSignedInWithData');

  if (isSignedInWithData) {
    dispatch(push(routeConstants.DELIVERY_ADDRESS_BOOK));
    return;
  }

  if (!isSignedInWithData) {
    const confirmedDeliveryAddress = get(getState().delivery, 'confirmedDeliveryAddress');
    const selectedDeliveryAddress = get(getState().user, 'selectedDeliveryAddress');
    const address = confirmedDeliveryAddress || selectedDeliveryAddress;
    const initValues = getFormInitialValuesFromAddress(address);

    dispatch(resetForm(DELIVERY_ADDRESS_FORM_ID, initValues));
    dispatch({ type: CHANGE_DELIVERY_ADDRESS });
  }
};

export const setShowDeliveryAddressForm = show => ({
  type: SET_SHOW_DELIVERY_ADDRESS_FORM,
  show,
});

export const giftReceiptSelected = isGiftReceiptSelected => (dispatch) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_GIFT_RECEIPT_SELECTED, {
    giftReceiptSelected: isGiftReceiptSelected,
  }));
};

export const giftMessagePopulated = isGiftMessagePopulated => (dispatch) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_GIFT_MESSAGE_POPULATED, {
    giftMessagePopulated: isGiftMessagePopulated,
  }));
};

export const onUseAddressButtonClick = () => async (dispatch, getState) => {
  const isSignedIn = getState().user.isSignedIn;
  const selectedDeliveryAddress = getState().user.selectedDeliveryAddress;

  dispatch(setScrollToDeliveryDetails(true));
  if (isSignedIn && !selectedDeliveryAddress.id) {
    return dispatch(createAddress({
      addressPayload: {
        addressee: selectedDeliveryAddress.addressee,
        address: selectedDeliveryAddress.address,
        phoneNumber: selectedDeliveryAddress.phoneNumber,
      },
    }));
  }

  return dispatch(setDeliveryAddressAndGetDeliveryMethods({
    address: selectedDeliveryAddress,
  }));
};
